import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { DataGrid } from '@/components/DataGrid/DataGrid';
import { useClientDataGrid } from '@/components/DataGrid/useClientDataGrid';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useAlert } from '@/components/ConfirmDialog';
import { CountryThresholdsPanel } from './CountryThresholdsPanel';
import { DealersPanel } from './DealersPanel';
import { DealerTextFilter } from './DealerTextFilter';
import { downloadExport, ExportSessionExpiredError } from './exportDownload';
import { TakataDealerMap, type TakataDealerMapLabels } from './TakataDealerMap';
import { waveLabel } from './takataFormat';
import {
  categoryOf,
  UNKNOWN_CATEGORY_COLOR,
  useTakataDealersDashboard,
  type TakataDealer,
} from './useTakataDealers';
import '@/styles/stla/page-shell.scss';
import '@/styles/stla/stla_shared.scss';
import '@/styles/stla/stla_datatables.scss';
import '@/styles/stla/stla_dashboard.scss';
import '@/styles/stla/dealer-dashboard.scss';

/**
 * Replaces `Pages/Stellantis/TakataDealerList.cshtml` at `/stla/takata`.
 *
 * The wave arrives whole and every filter runs in the browser, exactly as the
 * Razor page worked once `window.dealersData` was in hand — the difference is
 * that the set now comes from `GET /api/stla/takata/dealers` instead of a
 * serialised `<script>` block. Map, KPI tiles and table all read the same
 * filtered array, which is what keeps them consistent with each other.
 *
 * Two behaviours the Razor page had that are deliberately not reproduced:
 *
 *  - The dealer-name box is now wired. It was rendered with a `<datalist>` and a
 *    clear button but no event listener at all, so typing in it did nothing;
 *    worse, its clear button was caught by the `.clear-filter-btn` selector and
 *    cleared the *city* box instead. It filters by dealer name here.
 *  - `window.cityToCountryMap` is not requested. The Razor page serialised it on
 *    every load and only ever passed its length to `console.log`; the one screen
 *    that reads it is DealerList.
 */

/** Dealer-service values are stored, not localized — the filter matches them literally. */
const DEALER_SERVICE_OPTIONS = ['SERVICE ONLY', 'DEALER'];

/** Free-text filters only bite from the second character, as the Razor page did. */
const MIN_TEXT_FILTER_LENGTH = 2;

interface DealerFilters {
  country: string;
  dealerService: string;
  performance: string;
  city: string;
  dealer: string;
}

const NO_FILTERS: DealerFilters = {
  country: '',
  dealerService: '',
  performance: '',
  city: '',
  dealer: '',
};

function matchesText(value: string | null | undefined, term: string): boolean {
  if (term.length < MIN_TEXT_FILTER_LENGTH) return true;
  return (value ?? '').toLowerCase().includes(term);
}

export function TakataDealersPage() {
  const { t } = useTranslation('mystery/dealerlist');
  const alert = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();

  // The wave stays in the URL, as it was when the selector submitted a GET form,
  // so a link to one wave still resolves to it.
  const waveParam = searchParams.get('wave');
  const { data, isLoading, isError } = useTakataDealersDashboard(waveParam);
  const wave = waveParam ?? data?.selectedWave ?? null;

  const [filters, setFilters] = useState<DealerFilters>(NO_FILTERS);
  const debouncedFilters = useDebouncedValue(filters, 250);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [thresholdsPanelOpen, setThresholdsPanelOpen] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const debouncedTableSearch = useDebouncedValue(tableSearch, 250);

  const setFilter = useCallback((key: keyof DealerFilters, value: string) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);

  const dealers = useMemo(() => data?.dealers ?? [], [data]);
  const categories = useMemo(() => data?.performanceCategories ?? {}, [data]);

  const filteredDealers = useMemo(() => {
    const city = debouncedFilters.city.trim().toLowerCase();
    const dealerName = debouncedFilters.dealer.trim().toLowerCase();
    const performance = debouncedFilters.performance;

    return dealers.filter((dealer) => {
      if (debouncedFilters.country && dealer.country !== debouncedFilters.country) return false;
      if (debouncedFilters.dealerService && dealer.dealerService !== debouncedFilters.dealerService)
        return false;
      if (performance !== '' && dealer.categoryIndex !== Number(performance)) return false;
      if (!matchesText(dealer.city, city)) return false;
      if (!matchesText(dealer.name, dealerName)) return false;
      return true;
    });
  }, [dealers, debouncedFilters]);

  /**
   * KPI tiles, recomputed from whatever the filters left. The server sends its
   * own KPI block for the whole wave, but the Razor page overwrote those three
   * numbers with this calculation as soon as the page loaded, so the wave-wide
   * figures were only ever visible for a frame.
   */
  const kpis = useMemo(() => {
    const booked = filteredDealers.filter(
      (d) => d.averageWaitDays != null && (d.categoryIndex ?? 0) > 0,
    );

    const averageWaitDays =
      booked.length > 0
        ? booked.reduce((sum, d) => sum + (d.averageWaitDays ?? 0), 0) / booked.length
        : 0;

    return {
      averageWaitDays,
      totalAppointments: filteredDealers.filter((d) => d.hasAppointment).length,
      totalCalls: filteredDealers.reduce((sum, d) => sum + (d.totalCalls ?? 0), 0),
    };
  }, [filteredDealers]);

  /** Thresholds for the selected country, shown under the KPI tiles. */
  const selectedCountryThresholds = useMemo(() => {
    if (!filters.country || !data) return null;
    const code = data.countryCodes[filters.country];
    const thresholds = code ? data.thresholdsByCountry[code] : undefined;
    return thresholds && thresholds.length > 0 ? thresholds : null;
  }, [filters.country, data]);

  const mapLabels = useMemo<TakataDealerMapLabels>(
    () => ({
      city: t('Dashboard.Popup.City', 'City'),
      address: t('Dashboard.Popup.Address', 'Address'),
      phone: t('Dashboard.Popup.Phone', 'Phone'),
      brand: t('Dashboard.Popup.Brand', 'Brand'),
      performance: t('Dashboard.Popup.Performance', 'Performance'),
      wait: t('Dashboard.Popup.Wait', 'Wait'),
      calls: t('Dashboard.Popup.Calls', 'Calls'),
      days: t('Dashboard.Popup.Days', 'days'),
      noAppointment: t('Dashboard.Popup.NoAppointment', 'No appointment'),
      performancePerBrand: t('Dashboard.Popup.PerformancePerBrand', 'Performance per brand'),
    }),
    [t],
  );

  const columns = useMemo<ColumnDef<TakataDealer, unknown>[]>(
    () => [
      {
        id: 'country',
        accessorKey: 'country',
        header: t('Dashboard.Filters.Country', 'Country'),
        size: 90,
        meta: { align: 'center' },
      },
      {
        id: 'physOut',
        accessorKey: 'physOut',
        header: t('PhysOut', 'PhysOut'),
        size: 110,
        meta: { align: 'center' },
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: t('Regional.DealersPanel.TableHeaders.Dealer', 'Company Name'),
        size: 220,
      },
      {
        id: 'categoryIndex',
        accessorKey: 'categoryIndex',
        header: t('Dashboard.Filters.Performance', 'Performance'),
        size: 130,
        meta: { align: 'center' },
        cell: ({ row }) => {
          const category = categoryOf(categories, row.original.categoryIndex);
          return (
            <span
              className="performance-badge"
              style={{ backgroundColor: category?.backgroundColor ?? UNKNOWN_CATEGORY_COLOR }}
            >
              {category?.label ?? 'N/A'}
            </span>
          );
        },
      },
      {
        id: 'averageWaitDays',
        accessorKey: 'averageWaitDays',
        header: t('Regional.DealersPanel.TableHeaders.LeadBookingTime', 'Lead Booking Time'),
        size: 160,
        meta: { align: 'center' },
        cell: ({ row }) =>
          row.original.averageWaitDays != null ? (
            `${row.original.averageWaitDays} ${t('Dashboard.Popup.Days', 'days')}`
          ) : (
            <span style={{ color: 'var(--stla-500)' }}>
              {t('Dashboard.Popup.NoAppointment', 'No appointment')}
            </span>
          ),
      },
      {
        id: 'assignedBrand',
        accessorKey: 'assignedBrand',
        header: t('Dashboard.Popup.Brand', 'Brand'),
        size: 90,
      },
      { id: 'city', accessorKey: 'city', header: t('Dashboard.Popup.City', 'City'), size: 160 },
      {
        id: 'address',
        accessorKey: 'address',
        header: t('Dashboard.Popup.Address', 'Address'),
        size: 280,
      },
      { id: 'phone', accessorKey: 'phone', header: t('Dashboard.Popup.Phone', 'Phone'), size: 140 },
      { id: 'oic', accessorKey: 'oic', header: t('Oic', 'OIC'), size: 110, meta: { align: 'center' } },
    ],
    [t, categories],
  );

  const { gridProps } = useClientDataGrid<TakataDealer>({
    rows: filteredDealers,
    initialPageSize: 25,
    // Same default order the Razor grid opened on: shortest booking time first.
    initialSorting: [{ id: 'averageWaitDays', desc: false }],
    search: debouncedTableSearch,
    searchFields: ['country', 'physOut', 'name', 'assignedBrand', 'city', 'address', 'phone', 'oic'],
  });

  const [exporting, setExporting] = useState(false);

  /**
   * Exports the filtered set. The sidebar filters go out as query parameters so
   * the server can reproduce the same subset — the debounced values, not the
   * live ones, so the file matches the table rather than a keystroke ahead of it.
   */
  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (wave) params.set('wave', wave);
      for (const [key, value] of Object.entries(debouncedFilters)) {
        if (value) params.set(key, value);
      }
      await downloadExport(
        `/api/stla/takata/dealers/export?${params.toString()}`,
        'Takata_Dealers_Export.xlsx',
      );
    } catch (error) {
      if (error instanceof ExportSessionExpiredError) {
        window.location.href = '/auth-login';
        return;
      }
      // `t` here is the mystery/dealerlist namespace; the title comes from shared.
      void alert({
        variant: 'danger',
        title: t('shared:Message.Error', 'Error'),
        message: t('Grid.ExportError', 'Could not export data. Please try again.'),
      });
    } finally {
      setExporting(false);
    }
  }, [exporting, wave, debouncedFilters, t, alert]);

  const cities = data?.cities ?? [];
  const dealerNames = useMemo(
    () => Array.from(new Set(dealers.map((d) => d.name))).sort((a, b) => a.localeCompare(b)),
    [dealers],
  );

  if (isError) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        {t('Dashboard.LoadError', 'Could not load the dealer dashboard. Please try again.')}
      </div>
    );
  }

  return (
    // `dealer-dashboard` is what this screen's stylesheets are scoped to; see
    // styles/stla/page-shell.scss for why they have to be.
    <div className="regional-page dealer-dashboard">
      <div className="regional-container">
        {sidebarOpen && (
          <div className="sidebar">
            <div className="kpi-section">
              <h3>{t('Dashboard.KPI.MainTitle', 'Key figures')}</h3>
              <div className="kpi-grid">
                <div className="kpi-card primary">
                  <div className="kpi-label">
                    {t('Dashboard.KPI.AverageWaitTime', 'Average wait time')}
                  </div>
                  <div className="kpi-value">
                    <span>{kpis.averageWaitDays.toFixed(1)}</span> {t('Dashboard.KPI.Days', 'days')}
                  </div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">
                    {t('Dashboard.KPI.Appointments', 'Appointments')}
                  </div>
                  <div className="kpi-value">{kpis.totalAppointments}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">{t('Dashboard.KPI.TotalCalls', 'Total calls')}</div>
                  <div className="kpi-value">{kpis.totalCalls}</div>
                </div>
              </div>
            </div>

            {/* Only meaningful once a country is picked: the cutoffs differ per market. */}
            {selectedCountryThresholds && (
              <div className="thresholds-section visible">
                <h4>{t('Dashboard.Thresholds.Title', 'Thresholds')}</h4>
                <div className="thresholds-list">
                  {selectedCountryThresholds.map((threshold) => (
                    <div className="threshold-item" key={threshold.categoryIndex}>
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 3,
                          marginRight: '0.5rem',
                          backgroundColor:
                            categoryOf(categories, threshold.categoryIndex)?.backgroundColor ??
                            UNKNOWN_CATEGORY_COLOR,
                        }}
                      />
                      <span style={{ fontSize: '0.85rem', color: 'var(--stla-700)' }}>
                        {threshold.categoryName}: {threshold.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="filters-section">
              <h3>{t('Dashboard.Filters.Title', 'Filters')}</h3>

              <div className="filter-group">
                <label htmlFor="waveFilter">Wave</label>
                <select
                  id="waveFilter"
                  value={wave ?? ''}
                  onChange={(event) => setSearchParams({ wave: event.target.value })}
                >
                  {(data?.availableWaves ?? []).map((w) => (
                    <option key={w} value={w}>
                      {waveLabel(w)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="countryFilter">{t('Dashboard.Filters.Country', 'Country')}</label>
                <select
                  id="countryFilter"
                  value={filters.country}
                  onChange={(event) => setFilter('country', event.target.value)}
                >
                  <option value="">{t('Dashboard.Filters.AllCountries', 'All countries')}</option>
                  {(data?.countries ?? []).map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="dealerServiceFilter">
                  {t('Dashboard.Filters.DealerService', 'Dealer service')}
                </label>
                <select
                  id="dealerServiceFilter"
                  value={filters.dealerService}
                  onChange={(event) => setFilter('dealerService', event.target.value)}
                >
                  <option value="">{t('Dashboard.Filters.All', 'All')}</option>
                  {DEALER_SERVICE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="performanceFilter">
                  {t('Dashboard.Filters.Performance', 'Performance')}
                </label>
                <select
                  id="performanceFilter"
                  value={filters.performance}
                  onChange={(event) => setFilter('performance', event.target.value)}
                >
                  <option value="">
                    {t('Dashboard.Filters.AllPerformances', 'All performances')}
                  </option>
                  {Object.values(categories)
                    .sort((a, b) => a.categoryIndex - b.categoryIndex)
                    .map((category) => (
                      <option key={category.categoryIndex} value={category.categoryIndex}>
                        {category.label}
                      </option>
                    ))}
                </select>
              </div>

              <DealerTextFilter
                id="cityFilter"
                label={t('Dashboard.Filters.City', 'City')}
                placeholder={t('Dashboard.Filters.FindCity', 'Find a city')}
                clearTitle={t('Dashboard.Filters.Clear', 'Clear')}
                listId="cityList"
                options={cities}
                value={filters.city}
                onChange={(value) => setFilter('city', value)}
              />

              <DealerTextFilter
                id="dealerFilter"
                label={t('Dashboard.Filters.Dealer', 'Dealer')}
                placeholder={t('Dashboard.Filters.FindDealer', 'Find a dealer')}
                clearTitle={t('Dashboard.Filters.Clear', 'Clear')}
                listId="dealerList"
                options={dealerNames}
                value={filters.dealer}
                onChange={(value) => setFilter('dealer', value)}
              />

              <button
                type="button"
                className={`country-thresholds-toggle-btn${thresholdsPanelOpen ? ' active' : ''}`}
                onClick={() => setThresholdsPanelOpen((open) => !open)}
              >
                <span>
                  {thresholdsPanelOpen
                    ? t('ThresholdsPanel.HideButton', 'Hide Thresholds')
                    : t('ThresholdsPanel.ToggleButton', 'Show Thresholds')}
                </span>
              </button>

              <CountryThresholdsPanel
                thresholdsByCountry={data?.thresholdsByCountry ?? {}}
                open={thresholdsPanelOpen}
                onClose={() => setThresholdsPanelOpen(false)}
              />
            </div>
          </div>
        )}

        <div className="regional-main-content">
          <div className="map-container">
            <div className="map-controls">
              <button
                type="button"
                className="btn-map-control"
                onClick={() => setSidebarOpen((open) => !open)}
                title={t('Dashboard.Map.ToggleSidebar', 'Toggle sidebar')}
                aria-label={t('Dashboard.Map.SidebarAriaLabel', 'Toggle the filters sidebar')}
                aria-expanded={sidebarOpen}
              >
                <span aria-hidden="true">📋</span>
                <span>{t('Dashboard.Map.Sidebar', 'Sidebar')}</span>
              </button>
            </div>

            <TakataDealerMap
              dealers={filteredDealers}
              categories={categories}
              labels={mapLabels}
              // Both toggles change the map's box, and Leaflet has to re-measure.
              resizeToken={`${sidebarOpen}-${panelOpen}`}
            />
          </div>

          <DealersPanel
            open={panelOpen}
            onToggle={() => setPanelOpen((open) => !open)}
            toolbar={
              <>
                <input
                  type="search"
                  className="regional-dealers-search"
                  value={tableSearch}
                  onChange={(event) => setTableSearch(event.target.value)}
                  placeholder={t('Grid.Search', 'Search:')}
                  aria-label={t('Grid.Search', 'Search:')}
                />

                <div className="dt-buttons">
                  <button
                    type="button"
                    className="btn-export-excel"
                    onClick={handleExport}
                    disabled={exporting}
                  >
                    {exporting
                      ? t('Grid.Exporting', 'Exporting…')
                      : t('Grid.ExportExcel', 'Export Excel')}
                  </button>
                </div>
              </>
            }
          >
            <DataGrid<TakataDealer>
              {...gridProps}
              columns={columns}
              isLoading={isLoading}
              tableClassName="regional-dealers-table"
              getRowId={(row, index) => `${row.physOut}-${row.assignedBrand}-${index}`}
              pageSizeOptions={[10, 25, 50, 100]}
              emptyMessage={t('Grid.EmptyTable', 'No dealers available')}
            />
          </DealersPanel>
        </div>
      </div>
    </div>
  );
}
